/**
 * Healthcare Facility building type configuration.
 * Extracted from healthcareFacility/ vertical.
 */

// Points system (HDPS)
import {
  HC_PATHWAY_CAPS, HC_PRECONDITIONS, HC_FEATURES, HC_FEATURE_MAP,
  checkHcPreconditions, autoDetectHcFeatures, scoreHcSpace, scoreHcProject, getHcCertificationTier,
} from '../../healthcareFacility/engine/bhpsHealthcare';

// Financial engines
import {
  HC_PATHWAY_COEFFICIENTS, HC_ROLE_DEFAULTS, HC_STAFF_EVIDENCE, HC_STAFF_CHANNEL_TOOLTIPS,
  NURSE_BURNOUT_PARAMS, MED_ERROR_PARAMS, REPUTATIONAL_PARAMS, PATIENT_FALL_PARAMS,
  calculateHcStaffRoi,
} from '../../healthcareFacility/engine/financialStaffHealthcare';

import {
  PATIENT_ACUITY_TIERS, PATIENT_EVIDENCE, PATIENT_CHANNEL_TOOLTIPS,
  calculatePatientRoiByTier,
} from '../../healthcareFacility/engine/financialPatient';

// Neuroscience
import {
  HC_PATHWAY_MECHANISMS,
  computeTemporalOutcomesHealthcare,
} from '../../healthcareFacility/engine/neuroscienceHealthcare';

export default {
  type: 'healthcare',
  label: 'Healthcare Facility',
  shortLabel: 'Healthcare',
  color: '#0891B2',  // Cyan
  icon: 'hospital',

  // --- Unit of analysis ---
  unitLabel: 'Space',
  unitLabelPlural: 'Spaces',
  unitIdField: 'spaceId',

  // --- Space types ---
  spaceTypes: [
    { id: 'patient_room', label: 'Patient Room', icon: 'bed' },
    { id: 'icu', label: 'ICU', icon: 'heart' },
    { id: 'waiting_area', label: 'Waiting Area', icon: 'clock' },
    { id: 'nurse_station', label: 'Nurse Station', icon: 'user-nurse' },
    { id: 'corridor', label: 'Corridor', icon: 'arrows-alt-h' },
    { id: 'lobby', label: 'Lobby / Entry', icon: 'door-open' },
    { id: 'operating_room', label: 'Operating Room', icon: 'procedures' },
    { id: 'rehab', label: 'Rehabilitation', icon: 'walking' },
    { id: 'cafeteria', label: 'Cafeteria', icon: 'utensils' },
    { id: 'pharmacy', label: 'Pharmacy', icon: 'pills' },
    { id: 'garden', label: 'Healing Garden', icon: 'leaf' },
    { id: 'general', label: 'General', icon: 'building' },
  ],

  // --- Input fields (same as building-scale) ---
  inputFields: {
    daylight_lux: { label: 'Daylight (lux)', unit: 'lux', pathway: 'daylight' },
    noise_db: { label: 'Noise Level (dB)', unit: 'dB', pathway: 'noise' },
    pm25_ug_m3: { label: 'PM2.5 (µg/m³)', unit: 'µg/m³', pathway: 'air_quality' },
    visual_pattern_stress: { label: 'Visual Pattern Stress (0-1)', unit: '', pathway: 'visual_stress' },
    enrichment_score: { label: 'Enrichment Score (0-1)', unit: '', pathway: 'enrichment' },
    wayfinding_score: { label: 'Wayfinding Score (0-1)', unit: '', pathway: 'wayfinding' },
    movement_minutes_day: { label: 'Movement (min/day)', unit: 'min', pathway: 'movement' },
    nature_minutes_day: { label: 'Nature Exposure (min/day)', unit: 'min', pathway: 'nature' },
    nature_view: { label: 'Nature View', unit: 'boolean', pathway: 'nature' },
  },

  // --- Points system (HDPS) ---
  points: {
    name: 'HDPS',
    fullName: 'Healthcare Design Points Score',
    pathwayCaps: HC_PATHWAY_CAPS,
    preconditions: HC_PRECONDITIONS,
    features: HC_FEATURES,
    featureMap: HC_FEATURE_MAP,
    tiers: { platinum: 75, gold: 60, silver: 45, bronze: 30 },
    checkPreconditions: checkHcPreconditions,
    autoDetectFeatures: autoDetectHcFeatures,
    scoreSpace: scoreHcSpace,
    scoreProject: scoreHcProject,
    getCertificationTier: getHcCertificationTier,
  },

  // --- Financial model ---
  financial: {
    staffLabel: 'Hospital Staff',
    occupantLabel: 'Patient',
    staffPathwayCoefficients: HC_PATHWAY_COEFFICIENTS,
    staffRoleDefaults: HC_ROLE_DEFAULTS,
    staffEvidence: HC_STAFF_EVIDENCE,
    staffChannelTooltips: HC_STAFF_CHANNEL_TOOLTIPS,
    nurseBurnoutParams: NURSE_BURNOUT_PARAMS,
    medErrorParams: MED_ERROR_PARAMS,
    reputationalParams: REPUTATIONAL_PARAMS,
    patientFallParams: PATIENT_FALL_PARAMS,
    calculateStaffRoi: calculateHcStaffRoi,
    occupantAcuityTiers: PATIENT_ACUITY_TIERS,
    occupantEvidence: PATIENT_EVIDENCE,
    occupantChannelTooltips: PATIENT_CHANNEL_TOOLTIPS,
    calculateOccupantRoi: calculatePatientRoiByTier,
    staffChannels: [
      { name: 'Burnout: Absenteeism', key: 'burnout_absenteeism' },
      { name: 'Burnout: Presenteeism', key: 'burnout_presenteeism' },
      { name: 'Clinical Accuracy', key: 'clinical_accuracy' },
      { name: 'Efficiency', key: 'efficiency' },
      { name: 'Medication Errors', key: 'medication_errors' },
      { name: 'Patient Falls', key: 'patient_falls' },
      { name: 'Nurse Retention', key: 'retention' },
      { name: 'Reputational Impact', key: 'reputational_impact' },
    ],
    occupantChannels: [
      { name: 'HAI Reduction', key: 'hai' },
      { name: 'LOS Reduction', key: 'los' },
      { name: 'Pain / Opioid Reduction', key: 'pain' },
      { name: 'Readmission Reduction', key: 'readmission' },
    ],
    // Healthcare-specific: needs annualAdmissions and annualRevenue
    requiresAdmissions: true,
    requiresRevenue: true,
  },

  // --- Optimizer targets (stricter for healthcare) ---
  optimizerTargets: {
    daylight: {
      field: 'daylight_lux', target: 400, unit: 'lux',
      rationale: 'Above 250 lux minimum, practical optimum for hospital environments (Brown 2022)',
      action: 'Increase daylight exposure',
      details: 'Add window film removal, light shelves, or tunable LED panels to boost ambient lux',
      mechanism: 'Daylight activates ipRGC photoreceptors, driving circadian CREB signaling (Brown 2022)',
      category: 'moderate',
    },
    noise: {
      field: 'noise_db', target: 42, unit: 'dB',
      rationale: 'WHO hospital guideline; below 45 dB precondition (Morrison 2003)',
      action: 'Reduce ambient noise levels',
      details: 'Install acoustic ceiling tiles (NRC >= 0.75), silent door closers, sound masking',
      mechanism: 'Noise above 80 dB inhibits LTP and reduces BDNF by 26% at 110 dB (de Deus 2017)',
      category: 'quick_win',
    },
    air_quality: {
      field: 'pm25_ug_m3', target: 5, unit: 'ug/m3',
      rationale: 'WHO guideline achievable with HEPA filtration (Liu 2021)',
      action: 'Improve air filtration',
      details: 'Upgrade to MERV 17+ HEPA filters, add UV-C disinfection, increase ACH rate',
      mechanism: 'PM2.5 causes neuroinflammation; HR 1.15 per ug/m3 for AD risk (Liu 2021)',
      category: 'major_investment',
    },
    visual_stress: {
      field: 'visual_pattern_stress', target: 0.15, unit: 'score',
      rationale: 'Natural/varied patterns (Valentine 2025)',
      action: 'Reduce visual pattern stress',
      details: 'Replace uniform repetitive patterns with varied geometry, natural wood, curved elements',
      mechanism: 'Repetitive patterns at ~3 cpd trigger cortical hyperactivation (Valentine 2025)',
      category: 'quick_win',
    },
    enrichment: {
      field: 'enrichment_score', target: 0.7, unit: '/10',
      rationale: 'Achievable with targeted design interventions (Falkenberg 1992, Vega-Rosas 2024)',
      action: 'Increase environmental enrichment',
      details: 'Add artwork, varied seating, cognitive activities, diverse textures',
      mechanism: 'Enrichment drives BDNF via cognitive challenge (r=0.62, Vega-Rosas 2024)',
      category: 'moderate',
    },
    wayfinding: {
      field: 'wayfinding_score', target: 0.8, unit: 'score',
      rationale: 'Good signage + landmarks engage hippocampal place cells (Banner 2011)',
      action: 'Improve wayfinding and navigation',
      details: 'Add landmark features, clear signage hierarchy, color-coded zones',
      mechanism: 'Good wayfinding engages hippocampal spatial navigation (Banner 2011)',
      category: 'quick_win',
    },
    nature: {
      fields: { nature_minutes_day: 20, nature_view: true, nature_quality: 0.6 },
      unit: 'combined',
      rationale: '20 min optimal exposure; healing garden access (Hunter 2019)',
      action: 'Enhance nature access and views',
      details: 'Add window views to greenery, healing garden access, indoor plants',
      mechanism: 'Nature exposure reduces cortisol by 9.6%/hour (Hunter 2019)',
      category: 'moderate',
    },
    movement: {
      fields: { movement_minutes_day: 30, movement_intensity: 0.5 },
      unit: 'combined',
      rationale: '30 min walking; layout that encourages patient ambulation (Hendrich 2008)',
      action: 'Promote physical movement through design',
      details: 'Add walking paths with handrails, distance markers, decentralized amenities',
      mechanism: 'Exercise increases BDNF via PGC-1a/FNDC5/irisin pathway (Wrann 2013)',
      category: 'moderate',
    },
  },

  // --- Neuroscience labels ---
  neuroscience: {
    outcomeLabels: {
      dorsal: 'Cognitive & Spatial (Dorsal Hippocampus)',
      ventral: 'Emotional & Mood (Ventral Hippocampus)',
    },
    contextDescription: 'Hospital environments impact staff cognition and patient recovery.',
    pathwayMechanisms: HC_PATHWAY_MECHANISMS,
    computeTemporalOutcomes: computeTemporalOutcomesHealthcare,
    temporalLabels: {
      immediate: 'Immediate (hours)',
      shortTerm: 'Short-term (days-weeks)',
      longTerm: 'Long-term (weeks-months)',
    },
  },

  // --- Route prefix ---
  routePrefix: 'healthcare',
  projectTypeKey: 'healthcare_facility',

  // --- Demo data (lazy-loaded) ---
  demos: [
    { id: 'baylor', label: 'Baylor Scott & White', loader: () => import('../../healthcareFacility/engine/baylorDemo') },
    { id: 'cleveland', label: 'Cleveland Clinic', loader: () => import('../../healthcareFacility/engine/clevelandClinicDemo') },
  ],

  // --- Feature flags ---
  hasOptimizer: true,
  hasPopulationEditor: true,
};
